import StylishNewTable from 'components/DesignSystems/New/StylishNewTable';

import {
  TSCategoryToColor,
  TSCategoryToWindSpeed,
} from '../constants/TSCategoryToColor';

import { getStormCategory } from './EventDetails';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSilurianProjectionsFlag } from 'utils/featureFlags';

dayjs.extend(utc);

interface HurricaneDataPoint {
  unified_datetime_utc_formatted: string;
  unified_storm_maxwind: number;
  unified_storm_category: string;
  unified_data_time_type: string;
  storm_data_category: string;
}

type SilurianProjectionData = {
  datetime: string;
  geojson: string;
  id: string;
  model_datetime: string;
  type: string;
  weather_properties: {
    category: number[];
    type: string | null;
    vmax: number[];
    vmax_units: string;
  };
  storm_id_dt: string;
  properties: {
    storm_name: string;
    description: string;
    feature_type: string;
    forecast_source_info: string;
    forecast_time: string;
    lead_time: string;
    silurian_storm_id: string;
  };
};

type HurricaneEventDetailsProps = {
  event: {
    hurricane_data_points: HurricaneDataPoint[];
    id: string;
    name: string;
    hazard_name: string;
    hazard_data_category: string;
    geometry_type: string;
    instance_id: string;
  };
  layerState: {
    silurianProjectionVisibleToggle: boolean;
    silurianProjectionData: SilurianProjectionData[];
    setSilurianProjectionVisibleToggle: (value: boolean) => void;
  };
};

export function HurricaneEventDetails({
  event,
  layerState,
}: HurricaneEventDetailsProps) {
  const ethelRows = event.hurricane_data_points
    .filter((data: HurricaneDataPoint) =>
      data.storm_data_category.includes('Points')
    )
    .map((data: HurricaneDataPoint) => ({
      ...data,
      source: 'NWS',
    }));

  let silurianRows: HurricaneDataPoint[] = [];

  if (
    useSilurianProjectionsFlag() &&
    layerState.silurianProjectionVisibleToggle === true &&
    layerState?.silurianProjectionData?.length > 0
  ) {
    // Ensure that we cast the silurian data to be the same type as the legacy table rows

    silurianRows = layerState?.silurianProjectionData
      .filter(
        (data: SilurianProjectionData) =>
          data.type === 'Point' && data.storm_id_dt === event.id
      )
      .map((data: SilurianProjectionData) => {
        const maxWind = Number(
          (data.weather_properties?.vmax[0] * 1.944).toFixed(0)
        ); // Convert from m/s to knots
        const category =
          Object.keys(TSCategoryToWindSpeed).find((key) => {
            const range =
              TSCategoryToWindSpeed[key as keyof typeof TSCategoryToWindSpeed];
            if (maxWind >= range[0] && maxWind <= range[1]) {
              return key;
            }
          }) || 'N/A';


        return {
          source: 'Silurian',
          unified_datetime_utc_formatted: dayjs(data.datetime)
            .utc()
            .format('YY-MM-DD HHmm'),
          unified_storm_maxwind: maxWind,
          unified_storm_category: category,
          unified_data_time_type: 'F',
          storm_data_category: 'Forecast Points',
          model_datetime: dayjs(data.model_datetime)
            .utc()
            .format('YY-MM-DD HHmm'),
        };
      });
  }

  const tableRows = [...ethelRows, ...silurianRows];

  tableRows.sort((a, b) => {
    const dateA = dayjs(a.unified_datetime_utc_formatted, 'YY-MM-DD HHmm');
    const dateB = dayjs(b.unified_datetime_utc_formatted, 'YY-MM-DD HHmm');
    return dateB.diff(dateA);
  });

  let highlightedRowIndex = tableRows.findIndex(
    (p) => p.storm_data_category === 'Observed Points'
  );
  if (highlightedRowIndex === -1) highlightedRowIndex = tableRows.length - 1;

  const column = [
    {
      dataField: 'unified_datetime_utc_formatted',
      text: 'Date (UTC)',
      attrs: { title: 'Date (UTC)' },
    },
    {
      dataField: 'unified_storm_maxwind',
      text: 'Wind Speed (KT)',
      attrs: { title: 'Wind Speed (KT)' },
      formatter: (
        cell: any,
        row: { unified_storm_category: string; unified_storm_maxwind: string }
      ) => {
        const color =
          TSCategoryToColor[
            row.unified_storm_category as keyof typeof TSCategoryToColor
          ];
        return (
          <div
            className="EventDetailsHurricaneWindSpeedCell"
            style={{ color: color }}
          >
            {row.unified_storm_maxwind}
          </div>
        );
      },
    },
    {
      dataField: 'unified_storm_category',
      text: 'Category',
      attrs: (cell: any, row: { unified_storm_maxwind: string }) => {
        const category = getStormCategory(row?.unified_storm_maxwind);
        return { title: category.tooltip };
      },
      formatter: (
        cell: any,
        row: { unified_storm_category: string; unified_storm_maxwind: number }
      ) => {
        const color =
          TSCategoryToColor[
            row.unified_storm_category as keyof typeof TSCategoryToColor
          ];
        const category = getStormCategory(row?.unified_storm_maxwind);
        return (
          <div
            className="EventDetailsHurricaneWindSpeedCell"
            style={{ color: color }}
          >
            {category.category}
          </div>
        );
      },
    },
    {
      dataField: 'unified_data_time_type',
      text: 'Forecast/ Observed',
      attrs: (cell: any, row: { source: string, model_datetime: string }) => {
        return { title: row.model_datetime || row.source };
      },
      formatter: (
        cell: any,
        row: { unified_data_time_type: string; source: string }
      ) => {
        const isForecast = row.unified_data_time_type === 'F';
        return (
          <div className="EventDetailsHurricaneWindSpeedCell">
            {row.unified_data_time_type} {isForecast && `(${row.source})`}
          </div>
        );
      },
    },
  ];

  const rowClasses = (row: any, rowIndex: number) => {
    if (rowIndex === highlightedRowIndex) {
      return 'nonSelectableRow'; // Apply custom class to the non-selectable row
    }
    return '';
  };

  return (
    <>
      <div>
        <StylishNewTable
          keyField={'id'}
          rows={tableRows}
          columns={column}
          classes="mb-4 table-hurricane"
          rowClasses={rowClasses}
        />
      </div>
    </>
  );
}

export default HurricaneEventDetails;
